import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../api/axios';
import { Printer, Download, Share2, CheckCircle, MapPin, Phone, Mail, Clock, Calendar } from 'lucide-react';
import { numberToWords } from '../../utils/numberToWords';

const PublicInvoiceView = () => {
    const params = useParams();
    const token = params.token || params['*'];
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                // The URL matches the backend path we just added
                const response = await axios.get(`/api/billing/public/invoices/${token}/`);
                setInvoice(response.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching public invoice:", err);
                setError(err.response?.data?.detail || "Invoice not found or expired.");
                setLoading(false);
            }
        };
        fetchInvoice();
    }, [token]);

    const handlePrint = () => window.print();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium animate-pulse">Loading Digital Invoice...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center">
                <div className="max-w-md bg-white p-8 rounded-3xl shadow-xl border border-red-50">
                    <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 rotate-180" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a href="/" className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all">
                        Back to Home
                    </a>
                </div>
            </div>
        );
    }

    const { company_details: company, customer } = invoice;
    const items = invoice.items || [];
    const subtotal = parseFloat(invoice.subtotal) || 0;
    const totalAmount = parseFloat(invoice.total_amount) || 0;
    const cgst = parseFloat(invoice.cgst_amount) || 0;
    const sgst = parseFloat(invoice.sgst_amount) || 0;
    const igst = parseFloat(invoice.igst_amount) || 0;
    const taxRate = parseFloat(invoice.tax_rate) || 0;

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-lexend">
            {/* Header / Actions */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-10 print:hidden">
                <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white">
                            <CheckCircle className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="font-bold text-gray-900 leading-none">Digital Invoice</h1>
                            <p className="text-xs text-gray-400 mt-1">Ref: {invoice.invoice_number}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={handlePrint}
                            className="p-2.5 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors border border-gray-100"
                            title="Print / Save PDF"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button 
                            className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-100"
                            title="Share"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Invoice Content */}
            <div className="max-w-3xl mx-auto px-4 pt-8 print:pt-0">
                <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden print:shadow-none print:border-none">
                    {/* Top Branding Bar */}
                    <div className="h-2 bg-gradient-to-r from-blue-600 to-green-500"></div>
                    
                    <div className="p-8 md:p-12">
                        {/* Company & Header */}
                        <div className="flex flex-col md:flex-row justify-between gap-8 mb-12">
                            <div>
                                <h2 className="text-3xl font-black text-gray-900 tracking-tight mb-4 uppercase">
                                    {company?.company_name || "BUSINESS NAME"}
                                </h2>
                                <div className="space-y-1.5 text-gray-500 text-sm max-w-xs leading-relaxed">
                                    {company?.street_address && <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> {company.street_address}</p>}
                                    {company?.city && <p>{company.city}, {company.state} {company.postal_code}</p>}
                                    {company?.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {company.phone}</p>}
                                    {company?.tax_id && <p className="font-bold text-gray-900 mt-2">GSTIN: {company.tax_id}</p>}
                                </div>
                            </div>
                            <div className="text-right md:text-right flex flex-col items-end">
                                <div className="inline-block px-4 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-black tracking-widest uppercase mb-4">
                                    Tax Invoice
                                </div>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between md:justify-end gap-4">
                                        <span className="text-gray-400">Invoice No:</span>
                                        <span className="font-bold text-gray-900">{invoice.invoice_number}</span>
                                    </div>
                                    <div className="flex justify-between md:justify-end gap-4">
                                        <span className="text-gray-400">Date:</span>
                                        <span className="font-bold text-gray-900">{new Date(invoice.invoice_date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex justify-between md:justify-end gap-4">
                                        <span className="text-gray-400">Status:</span>
                                        <span className={`font-bold uppercase text-[10px] px-2 py-0.5 rounded ${
                                            invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                        }`}>{invoice.payment_status}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Info */}
                        <div className="bg-gray-50 rounded-2xl p-6 mb-12 flex flex-col md:flex-row justify-between gap-6 border border-gray-100">
                            <div>
                                <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-2">Billed To</p>
                                <h3 className="font-bold text-gray-900 text-lg">{customer?.name || "Walk-in Customer"}</h3>
                                {customer?.phone && <p className="text-sm text-gray-500 mt-1">{customer.phone}</p>}
                            </div>
                            {customer?.gstin && (
                                <div className="text-right">
                                    <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest mb-1">Customer GSTIN</p>
                                    <p className="font-bold text-gray-900">{customer.gstin}</p>
                                </div>
                            )}
                        </div>

                        {/* Items Table */}
                        <div className="mb-12 overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-gray-100">
                                        <th className="py-4 font-black text-[10px] uppercase text-gray-400 tracking-widest">Description</th>
                                        <th className="py-4 px-4 font-black text-[10px] uppercase text-gray-400 tracking-widest text-right">Qty</th>
                                        <th className="py-4 px-4 font-black text-[10px] uppercase text-gray-400 tracking-widest text-right">Price</th>
                                        <th className="py-4 font-black text-[10px] uppercase text-gray-400 tracking-widest text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => (
                                        <tr key={idx} className="border-b border-gray-50 group hover:bg-gray-50/50 transition-colors">
                                            <td className="py-4">
                                                <p className="font-bold text-gray-900">{item.product_name}</p>
                                                {item.variant_name && <p className="text-[10px] text-gray-400 uppercase font-medium">{item.variant_name}</p>}
                                            </td>
                                            <td className="py-4 px-4 text-right font-medium text-gray-600">{parseFloat(item.quantity)}</td>
                                            <td className="py-4 px-4 text-right font-medium text-gray-600">₹{parseFloat(item.unit_price).toLocaleString()}</td>
                                            <td className="py-4 text-right font-bold text-gray-900">₹{parseFloat(item.line_total).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary */}
                        <div className="flex flex-col md:flex-row justify-between gap-12">
                            <div className="flex-1">
                                <div className="bg-blue-50/50 rounded-2xl p-4 border border-blue-50">
                                    <p className="text-[10px] uppercase font-black text-blue-400 tracking-widest mb-2">Amount in Words</p>
                                    <p className="text-sm font-bold text-blue-900 italic">
                                        {numberToWords(totalAmount)} Only
                                    </p>
                                </div>
                                <div className="mt-8 flex items-center gap-2 text-gray-400 text-xs italic">
                                    <Clock className="w-3.5 h-3.5" />
                                    <span>Generated on {new Date(invoice.created_at).toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="w-full md:w-64 space-y-3">
                                <div className="flex justify-between text-gray-500 text-sm">
                                    <span>Subtotal:</span>
                                    <span className="font-bold">₹{subtotal.toLocaleString()}</span>
                                </div>
                                {(cgst > 0 || sgst > 0 || igst > 0) && (
                                    <div className="space-y-2 py-2 border-y border-gray-50">
                                        {cgst > 0 && <div className="flex justify-between text-xs text-gray-400"><span>CGST ({(taxRate)/2}%):</span><span>₹{cgst.toFixed(2)}</span></div>}
                                        {sgst > 0 && <div className="flex justify-between text-xs text-gray-400"><span>SGST ({(taxRate)/2}%):</span><span>₹{sgst.toFixed(2)}</span></div>}
                                        {igst > 0 && <div className="flex justify-between text-xs text-gray-400"><span>IGST ({taxRate}%):</span><span>₹{igst.toFixed(2)}</span></div>}
                                    </div>
                                )}
                                <div className="flex justify-between items-center pt-3 border-t-2 border-gray-100">
                                    <span className="font-black text-gray-900 text-lg tracking-tight uppercase">Grand Total</span>
                                    <span className="font-black text-blue-600 text-2xl tracking-tighter">₹{totalAmount.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Message */}
                    <div className="bg-gray-50 p-6 text-center border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-400">
                            Thank you for shopping with us! This is a computer generated invoice.
                        </p>
                    </div>
                </div>
                
                {/* Branding */}
                <div className="mt-8 flex flex-col items-center gap-2 opacity-50 grayscale hover:grayscale-0 transition-all cursor-default print:hidden">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Powered By</p>
                    <span className="font-black text-blue-600 text-lg tracking-tighter">GIO BILLING</span>
                </div>
            </div>
        </div>
    );
};

export default PublicInvoiceView;
